import { CoverageSplitStatus } from "@redotech/redo-ab-testing-common/coverage-split-status";
import { CoverageTreatmentIdSource } from "@redotech/redo-ab-testing-common/coverage-treatment-id";
import { storefrontAbTestIdFormat } from "@redotech/redo-coverage-id";
import * as z from "zod";

const nullableRatio = z.number().nullable();

const dailyTrendPoint = z.object({
  metricDate: z.string(),
  conversionRate: nullableRatio,
  coverageAttachRate: nullableRatio,
});

/**
 * The AB testing v2 funnel is counted in sessions: sessions, then
 * addToCartSessions, checkoutStartSessions, widgetShownSessions, then orders.
 * The shopper counts are kept as raw numbers.
 */
export const coverageArmMetricView = z.object({
  coverageTreatmentId: z.string(),
  coverageTreatmentIdSource: z.nativeEnum(CoverageTreatmentIdSource),
  latestMetricDate: z.string(),
  assignedShoppers: z.number(),
  addToCartShoppers: z.number(),
  checkoutStartShoppers: z.number(),
  sessions: z.number(),
  orders: z.number(),
  coverageOrders: z.number(),
  returnsCoverageOrders: z.number(),
  packageProtectionOrders: z.number(),
  revenueInShopCurrency: z.number(),
  shopCurrencyCode: z.string().nullable(),
  widgetShownShoppers: z.number().nullable(),
  addToCartSessions: z.number().nullable(),
  checkoutStartSessions: z.number().nullable(),
  widgetShownSessions: z.number().nullable(),
  addToCartRate: nullableRatio,
  checkoutStartRate: nullableRatio,
  conversionRate: nullableRatio,
  coverageAttachRate: nullableRatio,
  returnsCoverageAttachRate: nullableRatio,
  packageProtectionAttachRate: nullableRatio,
  averageOrderValue: nullableRatio,
  revenuePerSession: nullableRatio,
  widgetShownRate: nullableRatio,
  windowStart: z.date(),
  dataThrough: z.date(),
  computedAt: z.date(),
  dailyTrend: z.array(dailyTrendPoint),
});

export const coverageSplitHeader = z.object({
  splitId: z.string(),
  name: z.string().nullable(),
  status: z.nativeEnum(CoverageSplitStatus),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
});

export const coverageSplitArmIdentity = z.object({
  treatmentId: z.string(),
  name: z.string(),
  weight: z.number(),
  /** The first arm by sort order; deleting that arm makes the next one the control. */
  isControl: z.boolean(),
  offersCoverage: z.boolean(),
});

export const getCoverageArmMetrics = {
  name: "getCoverageArmMetrics" as const,
  input: z.object({ splitId: storefrontAbTestIdFormat.schema }),
  output: z.object({
    split: coverageSplitHeader,
    arms: z.array(
      coverageSplitArmIdentity.extend({
        metrics: coverageArmMetricView.nullable(),
      }),
    ),
  }),
};
